import { CollapsibleBox, CollapsibleBoxContainer } from '@/components/shared/CollapsibleBox';
import { adjustContentVisibility, Section, SectionParagraph, SectionTitle } from '@/components/shared/Content';
import { Flex, useViewportSize } from '@lemonade-hq/cdk';
import { MEDIA_QUERIES } from '@lemonade-hq/nuis-atomic';
import { useAnalyticsContext } from '@lemonade-hq/nuiza';
import parse from 'html-react-parser';
import type React from 'react';
import { useContext, useEffect } from 'react';
import { useInView } from 'react-intersection-observer';
import styled from 'styled-components';
import { CarPageContext } from './CarPageContext';

export interface FaqInfoProps {
  question: string;
  answer: string;
  analytics: string;
}

export const SectionWrapperInner = styled(Flex).attrs({
  justifyContent: 'center',
  flexDirection: 'column',
})`
  ${MEDIA_QUERIES.tabletAndDesktop`
        max-width: 1144px;
        width: 100%;
    `}
`;

const StyledSection = styled(Section)`
  ${adjustContentVisibility};
`;

export interface FaqProps extends React.ComponentProps<'div'> {
  title: string;
  subtitle: string;
  data: (isMobileViewport: boolean) => FaqInfoProps[];
  darkBackground?: boolean;
  variant?: 'default' | 'gray';
  paddingBottom?: number | number[];
}

export const Faq: React.FC<FaqProps> = ({ title, subtitle, data, variant, paddingBottom, ...props }) => {
  const { analytics } = useContext(CarPageContext);
  const { trackEvent } = useAnalyticsContext();
  const { isMobileViewport } = useViewportSize();
  const { ref, inView } = useInView({
    triggerOnce: true,
    threshold: 0.5,
  });

  // biome-ignore lint/correctness/useExhaustiveDependencies: want to track event only once
  useEffect(() => {
    if (!inView) return;
    trackEvent(analytics.faqViewedEvent);
  }, [inView]);

  const faqTrackEvent = ({ eventName, analyticsId }: { eventName: string; analyticsId: string }) =>
    trackEvent(eventName, {
      product: analytics.product,
      analytics: analyticsId,
    });

  return (
    <StyledSection pt={[30, 60]} pb={paddingBottom ?? [30, 100]} px={20} {...props} ref={ref}>
      <SectionWrapperInner justifyContent="center" alignItems={['normal', 'center']}>
        <Flex mb="60px" flexDirection="column" gap="15px">
          <SectionTitle tabIndex={0}>{parse(title)}</SectionTitle>
          <SectionParagraph>{parse(subtitle)}</SectionParagraph>
        </Flex>
        <CollapsibleBoxContainer minBoxesHeight="300px" variant={variant}>
          {data(isMobileViewport).map(({ question, answer, analytics: analyticsId }) => (
            <CollapsibleBox
              key={question}
              title={question}
              content={answer}
              onMouseEnter={() =>
                faqTrackEvent({
                  eventName: 'faq.hovered',
                  analyticsId,
                })
              }
              onClick={() =>
                faqTrackEvent({
                  eventName: 'faq.clicked',
                  analyticsId,
                })
              }
            />
          ))}
        </CollapsibleBoxContainer>
      </SectionWrapperInner>
    </StyledSection>
  );
};
